// FontAwesome to Lucide icon mapping
// This file maps FontAwesome icons used in the public app to their Lucide equivalents

import {
  Plus,
  CreditCard,
  AlignJustify,
  ChevronDown,
  ChevronUp,
  Archive,
  ArrowDown,
  ArrowDownToLine,
  ArrowLeft,
  ArrowRight,
  ArrowLeftRight,
  RotateCcw,
  Move,
  ArrowUpDown,
  ExternalLink,
  Atom,
  Backpack,
  Ban,
  Bug,
  Menu,
  Filter,
  Bell,
  HelpCircle,
  Book,
  Bookmark,
  Globe,
  Calendar,
  ShoppingCart as ShoppingCartPlus,
  ShoppingCart,
  TrendingUp,
  BarChart3,
  TreePine,
  Check,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Circle,
  CheckCircle2,
  Dot,
  AlertCircle,
  AtSign,
  Play,
  X,
  Clock,
  Copy,
  Upload,
  Code,
  Settings,
  MessageSquare,
  Crosshair,
  Box,
  Minus,
  Dice6,
  Download,
  Pentagon as Polygon,
  Edit,
  MoreVertical,
  Mail,
  Eraser,
  AlertTriangle,
  Expand,
  Eye,
  EyeOff,
  Smile,
  File,
  FileDown,
  FileUp,
  Search,
  Folder,
  FolderDown,
  FolderX,
  Gamepad2,
  GripVertical,
  Hash,
  Hexagon,
  Home,
  Image,
  Images,
  Info,
  Gamepad,
  Keyboard,
  Lightbulb,
  ArrowUp,
  ArrowUpCircle,
  Link,
  List,
  MapPin,
  Lock,
  Map,
  Mic,
  MicOff,
  MinusCircle,
  Square,
  DollarSign,
  Moon,
  Group,
  Palette,
  Image as Panorama,
  Send,
  Pause,
  Pen,
  UserCheck,
  Users,
  Camera,
  PlayCircle,
  PlusCircle,
  Presentation,
  Printer,
  Puzzle,
  RectangleHorizontal,
  Redo,
  Construction,
  RotateCw,
  Save,
  Sliders,
  HelpCircle as Seal,
  ZoomOut,
  ZoomIn,
  Share,
  LogOut,
  Zap,
  SlidersHorizontal,
  Star,
  MapPin as Street,
  Subtitles,
  Target,
  Sun,
  Gauge,
  Tag,
  Tags,
  Grid3X3,
  Ticket,
  Minus as Tilde,
  Timer,
  XCircle,
  Trash,
  Triangle,
  Twitter,
  Undo,
  Unlock,
  User,
  User as UserTie,
  Video,
  VideoOff,
  Volume2,
  VolumeX,
  PersonStanding,
  Activity,
  Monitor,
  Smartphone,
  Trash2,
  DoorOpen,
  Heart,
  Film,
  Hand,
  Car,
  Layers,
  Ruler,
  Minimize,
  Plane,
  ShoppingBag,
  Route,
  Crown,
  Phone,
  MapPinned,
  Ellipsis,
  CalendarDays,
  Share2,
  FileText,
  type LucideIcon,
} from 'lucide-react';

// FontAwesome to Lucide mapping object
export const fontAwesomeToLucideMap: Record<string, LucideIcon> = {
  // Basic actions
  faAdd: Plus,
  faPlus: Plus,
  faSolidPlus: Plus,
  faMinus: Minus,
  faCheck: Check,
  faX: X,
  faXmark: X, // X mark (close icon)
  faClose: X,

  // Navigation
  faArrowLeft: ArrowLeft,
  faArrowRight: ArrowRight,
  faArrowUp: ArrowUp,
  faArrowDown: ArrowDown,
  faArrowUpRightFromSquare: ExternalLink,
  faArrowDownToLine: ArrowDownToLine,
  faChevronLeft: ChevronLeft,
  faChevronRight: ChevronRight,
  faChevronDown: ChevronDown,
  faChevronUp: ChevronUp,
  faAngleDown: ChevronDown,
  faAngleUp: ChevronUp,
  faCaretDown: ChevronDown,
  faCaretUp: ChevronUp,
  faCaretCircleDown: ChevronDown,
  faChevronCircleDown: ChevronDown,

  // Media controls
  faPlay: Play,
  faPause: Pause,
  faStop: Square,
  faVideo: Video,
  faFilm: Film,
  faVolumeUp: Volume2,
  faVolumeDown: Volume2,
  faVolumeOff: VolumeX,
  faVolumeMute: VolumeX,
  faVolumeSlash: VolumeX,

  // UI elements
  faBars: Menu,
  faMenu: Menu,
  faSearch: Search,
  faMagnifyingGlass: Search,
  faFilter: Filter,
  faSettings: Settings,
  faGear: Settings,
  faCog: Settings,
  faEllipsis: Ellipsis,
  faEllipsisV: MoreVertical,
  faMoreVert: MoreVertical,

  // Files and folders
  faFile: File,
  faFolder: Folder,
  faDownload: Download,
  faUpload: Upload,
  faSave: Save,
  faFilePdf: FileText,

  // Communication
  faEnvelope: Mail,
  faMail: Mail,
  faPhone: Phone,
  faMessage: MessageSquare,
  faComment: MessageSquare,
  faShare: Share2,
  faShareNodes: Share2,

  // Commerce
  faShoppingCart: ShoppingCart,
  faShoppingBag: ShoppingBag,
  faBasketShoppingSimple: ShoppingBag,
  faHeart: Heart,
  faStar: Star,

  // Location and maps
  faMap: Map,
  faMapPin: MapPin,
  faMapLocationDot: MapPinned,
  faLocation: MapPin,
  faRoute: Route,

  // Time and calendar
  faCalendar: Calendar,
  faCalendarAlt: CalendarDays,
  faClock: Clock,
  faTimer: Timer,

  // User and people
  faUser: User,
  faUsers: Users,
  faUserCheck: UserCheck,
  faCrown: Crown,

  // Tools and utilities
  faCamera: Camera,
  faRuler: Ruler,
  faLightbulb: Lightbulb,
  faExpand: Expand,
  faCompress: Minimize,
  faDrone: Plane,

  // Status and feedback
  faEye: Eye,
  faEyeOff: EyeOff,
  faEyeSlash: EyeOff,
  faCheckCircle: CheckCircle,
  faTimes: X,
  faExclamationTriangle: AlertTriangle,
  faWarning: AlertTriangle,
  faInfo: Info,
  faBell: Bell,

  // Edit and modify
  faEdit: Edit,
  faPen: Pen,
  faTrash: Trash,
  faTrash2: Trash2,
  faCopy: Copy,
  faUndo: Undo,
  faRedo: Redo,

  // Shapes and graphics
  faSquare: Square,
  faCircle: Circle,
  faTriangle: Triangle,
  faPolygon: Polygon,
  faHexagon: Hexagon,

  // Business and office
  faBook: Book,
  faBookmark: Bookmark,
  faPresentation: Presentation,
  faPrinter: Printer,
  faTag: Tag,
  faTags: Tags,

  // Technology
  faCode: Code,
  faGlobe: Globe,
  faBrowser: Globe,
  faMonitor: Monitor,
  faSmartphone: Smartphone,
  faGamepad: Gamepad,
  faKeyboard: Keyboard,

  // Miscellaneous
  faHome: Home,
  faLock: Lock,
  faUnlock: Unlock,
  faBan: Ban,
  faArchive: Archive,
  faBox: Box,
  faGift: Box,
  faPuzzle: Puzzle,
  faConstruction: Construction,
  faDoorOpen: DoorOpen,
  faActivity: Activity,
  faGauge: Gauge,
  faTachometer: Gauge,
  faSun: Sun,
  faMoon: Moon,
  faZoomIn: ZoomIn,
  faZoomOut: ZoomOut,
  faSliders: Sliders,
  faSlidersH: SlidersHorizontal,
  faGripVertical: GripVertical,
  faMove: Move,
  faArrowsUpDownLeftRight: Move,
  faResize: Move,
  faRotateLeft: RotateCcw,
  faRotateRight: RotateCw,
  faArrowsRotate: RotateCcw,
  faSync: RotateCcw,
  faRefresh: RotateCcw,

  // Additional eCommerce specific icons
  faCartCircleArrowDown: ChevronDown,
  faCartCircleArrowUp: ChevronUp,
  faVrCardboard: Box, // Using Box as placeholder for VR cardboard

  // Additional icons found in public app
  faCirclePlay: PlayCircle,
  faPlayCircle: PlayCircle,
  faPlusCircle: PlusCircle,
  faCheckCircle2: CheckCircle2,
  faXCircle: XCircle,
  faAlertCircle: AlertCircle,
  faInfoCircle: Info,
  faQuestionCircle: HelpCircle,
  faExclamationCircle: AlertCircle,

  // Loading and progress
  faSpinner: RotateCcw, // Using RotateCcw for spinner
  faCircleNotch: RotateCcw,

  // Additional navigation
  faAngleDoubleDown: ChevronDown,
  faAngleLeft: ChevronLeft,
  faAngleRight: ChevronRight,

  // Cloud and download
  faCloudDownloadAlt: Download,
  faCloudDownload: Download,

  // Location and maps
  faMapMarkerAlt: MapPin,
  faMapMarker: MapPin,

  // Interaction
  faHandPointer: Hand, // Need to import Hand from lucide

  // Real estate specific
  faBed: Home, // Using Home as placeholder for bed
  faShower: Home, // Using Home as placeholder for shower
  faCar: Car, // Need to import Car from lucide
  faRulerCombined: Ruler,
  faMoneyBillWave: DollarSign,

  // Social media
  faFacebook: Share2, // Using Share2 as placeholder for Facebook
  faTwitter: Share2, // Using Share2 as placeholder for Twitter
  faWhatsapp: Share2, // Using Share2 as placeholder for WhatsApp

  // Additional icons from BottomBar
  faMusic: Volume2, // Using Volume2 as placeholder for music
  faPauseCircle: Pause, // Pause circle
  faWalking: PersonStanding, // Using PersonStanding for walking
  faCouch: Home, // Using Home as placeholder for couch
  faLayerGroup: Layers, // Layers icon
  faCube: Box, // Using Box for cube
  faMapMarked: MapPin, // Map with marker
  faHouse: Home, // House icon
  faLanguage: Globe, // Using Globe for language
  faComments: MessageSquare, // Multiple comments

  // Additional icons from ClassicPublicAsset
  faBath: Home, // Using Home as placeholder for bath
  faGlobeAmericas: Globe, // Globe Americas
  faInstagram: Share2, // Using Share2 as placeholder for Instagram
  faLinkedin: Share2, // Using Share2 as placeholder for LinkedIn

  // Additional icons from video Toolbar
  faVideoSlash: VideoOff, // Video off/slash
  faMicrophoneSlash: MicOff, // Microphone off/slash
  faDesktop: Monitor, // Desktop/monitor
  faUserGroup: Users, // User group (using Users)
  faEllipsisVertical: MoreVertical, // Vertical ellipsis

  // Additional icons from TourConfigs
  faClosedCaptioning: Subtitles, // Closed captioning/subtitles
  faLongArrowAltRight: ArrowRight, // Long arrow right

  // Additional icons from NoteDescriptionField
  faCircleExclamation: AlertCircle, // Circle with exclamation
  faCircleXmark: XCircle, // Circle with X mark
  faPaperPlaneTop: Send, // Paper plane top (using Send)
  faAt: AtSign, // @ symbol

  // Additional icons from Minimap
  faBullseye: Target, // Bullseye/target icon
  faCameraWeb: Camera, // Web camera (using Camera)
  faSearchPlus: ZoomIn, // Search plus (zoom in)
  faSearchMinus: ZoomOut, // Search minus (zoom out)
  faArrowAltCircleUp: ArrowUpCircle, // Arrow alt circle up

  // Additional icons from MondelezTabletVideo
  faVolume: Volume2, // Volume icon (base volume)

  // Additional icons from Notes
  faLocationCrosshairs: Crosshair, // Location crosshairs
  faXmarkLarge: X, // Large X mark (using X)

  // Additional icons from VolumeControls
  faMinusCircle: MinusCircle, // Minus circle

  // Additional icons from FloorPlans
  faFileArrowDown: Download, // File arrow down (download)

  // Additional icons from Gallery
  faFolderArrowDown: FolderDown, // Folder arrow down

  // Additional icons from Navigation components
  faXTwitter: X, // X (formerly Twitter)

  // Additional icons from Navigation search components
  faArrowUpArrowDown: ArrowUpDown, // Arrow up arrow down (sort)

  // Additional icons from Notes components
  faListUl: List, // List with bullets

  // Additional icons from Notes card actions
  faArrowRotateLeft: RotateCcw, // Arrow rotate left (counter-clockwise)
  faPenToSquare: Edit, // Pen to square (edit)
  faTrashCan: Trash2, // Trash can
  faArrowRightFromBracket: LogOut, // Arrow right from bracket (logout)

  // Additional icons from eCommerce Store
  faSortDown: ChevronDown, // Sort down (using ChevronDown)

  // Additional icons from Avatar IntroPopup
  faSquareFull: Square, // Square full (filled square)

  // Additional social media icons from eCommerce About
  faFacebookF: Share2, // Facebook F (using Share2 as placeholder)
  faLinkedinIn: Share2, // LinkedIn In (using Share2 as placeholder)

  // Additional icons from FlowsMenu
  faAtomSimple: Atom, // Atom simple

  // Additional icons from SortMenu
  faBarsFilter: SlidersHorizontal, // Bars filter (using SlidersHorizontal)

  // Additional icons from GuideModal
  faRunning: Zap, // Running (using Zap as placeholder for activity)

  // Additional icons from Avatar components
  faArrowLeftLong: ArrowLeft, // Long arrow left (using ArrowLeft)
};

// Helper function to get Lucide icon from FontAwesome icon name
export function getLucideIcon(fontAwesomeIconName: string): LucideIcon | undefined {
  return fontAwesomeToLucideMap[fontAwesomeIconName];
}

// Export commonly used Lucide icons for direct import
export {
  Plus,
  Minus,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Menu,
  Settings,
  Search,
  Edit,
  Trash,
  Eye,
  EyeOff,
  Home,
  User,
  Bell,
  Calendar,
  File,
  Folder,
  Image,
  Video,
  Play,
  Pause,
  Volume2,
  Star,
  Share2 as Share,
  Download,
  Upload,
  Copy,
  Save,
  type LucideIcon,
};
